import { Injectable } from '@angular/core';

import { API_ENDPOINTS, UrlConstants } from '@varsity/commons';
import {
    HttpHelperService, TokenService, EventsSearchResult,
    EventTicketsResponse, PricePoint, Confirmation, Package,
    PurchaseConfirmation, LoggingService, Ticket, TransferRecipient, TransferResponse, SectionLayout,
    StadiumTicket
} from '@varsity/services';
import { Observable } from "rxjs/Rx";
import { Merchandise } from '../events/events.model';
import { useAnimation } from '@angular/core/src/animation/dsl';
import { Select } from 'ionic-angular';
import { release } from 'os';

/*
  Generated class for the TicketsService provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class TicketsService {

    constructor(
        private httpHelperService: HttpHelperService,
        private loggingService: LoggingService,
        private tokenService: TokenService
    ) {
        this.loggingService.log('Hello TicketsService Provider');
    }

    getEventTickets(eventID: number, dbName: string, promoCode: string = ""): Observable<EventTicketsResponse> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getEventTickets;
        if (eventID) {
            let promise = this.httpHelperService
                .get<EventTicketsResponse>(url, {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "EvtID": eventID,
                        "PromoCode": promoCode,
                        "ReferringURL": "",
                        "TrackingInfo": "",
                        "SchoolDirectFlag": false,
                        "DBName": dbName
                    }
                });
            return promise;
        }

        return Observable.empty<EventTicketsResponse>();
    }

    getSectionLayout(filterPrices: PricePoint[], eventID: number, level: string, section: string, priceRating: string, dbName: string, promoCode: string = "", releaseSeats: boolean = false): Observable<SectionLayout> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.sectionUrl;

        let params = {
            "X-Token": this.tokenService.getToken(),
            "EventID": eventID,
            "Level": level,
            "Section": section,
            "PriceRating": priceRating,
            'NumTickets': '0',
            'Width': '200',
            'Height': '200',
            'MobileLayout': 'true',
            "DBName": dbName,
            "ReleaseSeats": releaseSeats
        }

        if (promoCode) {
            (params as any).PromoCode = promoCode;
        }

        let promise = this.httpHelperService
            .post<SectionLayout>(url, filterPrices, {
                params: params
            });

        return promise;
    }

    getSectionSeason(sponsor: number, seasonPassID: string, level: string, section: string, priceRating: string, numTickets: number, dbName: string, promoCode?: string, referringUrl: string = "", flexEvents: string = ""): Observable<SectionLayout> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.seasonSectionUrl;
        let requestParams = {
            "X-Token": this.tokenService.getToken(),
            "SponsorID": sponsor,
            "SeasonPassID": seasonPassID,
            "Level": level,
            "Section": section,
            "PriceRating": priceRating,
            "NumTickets": '0',
            'Width': '200',
            'Height': '200',
            "DBName": dbName
        }

        if (promoCode) {
            requestParams["PromoCode"] = promoCode;
        }
        if (referringUrl !== "") {
            requestParams["ReferringURL"] = referringUrl;
        }
        if (flexEvents !== "") {
            requestParams["FlexEvents"] = flexEvents;
        }
        if (sponsor > 0) {
            let promise = this.httpHelperService
                .get<SectionLayout>(url, {
                    params: requestParams
                });

            return promise;
        }
    }

    getPackagesPricing(sponsorID: number, seasonPassIDs: string, promoCode: string = "", dbName: string): Observable<EventTicketsResponse> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getPackagesPricing;
        if (sponsorID && seasonPassIDs.length > 0) {
            let promise = this.httpHelperService
                .get<EventTicketsResponse>(url, {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "SponsorID": sponsorID,
                        "SeasonPassIDs": seasonPassIDs,
                        "ReferringURL": "",
                        "PromoCode": promoCode,
                        "DBName": dbName
                    }
                });

            return promise;
        }
    }

    getAddOnEvents(eventID: number): Observable<EventsSearchResult[]> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getAddOnEvents;
        if (eventID) {
            let promise = this.httpHelperService
                .get<EventsSearchResult[]>(url, {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "EventID": eventID,
                        "AddOnFlag": "true",
                        "TrackingInfo": "",
                        "SchoolFlag": "true",
                        "MobileFlag": "true"
                    }
                });
            return promise;
        }

        return Observable.empty<EventsSearchResult[]>();
    }

    // TODO add confirmation object as param for the API request.
    findSchoolTickets(pricePoints: PricePoint[], nti: Merchandise[], tickets: Ticket[], ticketPrices: PricePoint[], confirmation: Confirmation, dbName: string): Observable<Confirmation> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.findSchoolTickets;
        if (pricePoints || pricePoints == null) {
            let promise = this.httpHelperService
                .post<Confirmation>(url,
                    {
                        pricepoints: pricePoints,
                        curConfirmation: confirmation,
                        nti: nti,
                        selectASeatFiltered: {
                            seltickets: tickets,
                            filterPrices: ticketPrices
                        }
                    },
                    {
                        params: {
                            "X-Token": this.tokenService.getToken(),
                            "SchoolFlag": "1",
                            "DBName": dbName
                        }
                    });
            return promise;
        }
    }

    findSchoolPackages(pricePoints: PricePoint[], nti: Merchandise[], confirmation: Confirmation, dbName: string): Observable<Confirmation> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.findSchoolPackages;
        if (pricePoints || pricePoints == null) {
            let promise = this.httpHelperService
                .post<Confirmation>(url,
                    {
                        pricepoints: pricePoints,
                        curConfirmation: confirmation,
                        nti: nti
                    },
                    {
                        params: {
                            "X-Token": this.tokenService.getToken(),
                            "SchoolFlag": "1",
                            "DBName": dbName
                        }
                    });
            return promise;
        }
    }

    reserveSeats(pricePoints: PricePoint[], tickets: Ticket[],
        confirmation: Confirmation, dbName: string): Observable<Confirmation> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.reserveSeats;
        let promise = this.httpHelperService
            .post<Confirmation>(url,
                {
                    filterPrices: pricePoints,
                    curConfirmation: confirmation,
                    seltickets: tickets
                },
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "MobileFlag": "true",
                        "DBName": dbName
                    }
                });
        return promise;
    }

    reserveSeatsSeason(seasonpass: Package, tickets: Ticket[], dbName: string): Observable<Confirmation> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.reserveSeatsSeason;
        let promise = this.httpHelperService
            .post<Confirmation>(url,
                {
                    seasonpass: seasonpass,
                    selectaseatticket: tickets
                },
                {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        'SeasonFlag': true,
                        'releaseseats': true,
                        'DBName': dbName
                    }
            });
        return promise;
    }

    confirmTickets(confirmation: Confirmation, stripeSessionToken: string, dbName: string, isMobile: boolean): Observable<PurchaseConfirmation> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.purchaseSchoolTickets;

        if (confirmation) {
            let promise = this.httpHelperService
                .post<PurchaseConfirmation>(url, confirmation, {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "Token": stripeSessionToken,
                        "SchoolFlag": "1",
                        "MobileFlag": isMobile,
                        "DBName": dbName
                    }
                });
            return promise;
        }

        return Observable.empty<PurchaseConfirmation>();
    }

    getAccountTickets(eventID: number, sponsorID: number, confirmationID?: number, transferOnlyFlag: boolean = false): Observable<Ticket[]> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getAccountTickets;
        if (eventID && sponsorID) {
            let promise = this.httpHelperService
                .get<Ticket[]>(url, {
                    params: {
                        "X-Token": this.tokenService.getToken(),
                        "EventID": eventID,
                        "SponsorID": sponsorID,
                        "CID": confirmationID,
                        "TransferOnly": transferOnlyFlag
                    }
                });
            return promise;
        }

        return Observable.empty<Ticket[]>();
    }

    transferTickets(recipientInfo: TransferRecipient, selectedTickets: Ticket[], confirmationID: number, sendReceiptFlag: boolean = false, transferMethod: string, dbName: string): Observable<TransferResponse> {
        let url = UrlConstants.tokenBOUrl;
        if (selectedTickets && selectedTickets.length > 0 && recipientInfo.FirstName && recipientInfo.LastName) {
            let promise;
            let useEmailTransfer = (transferMethod && transferMethod == "email");
            if (useEmailTransfer) {
                url += API_ENDPOINTS.transferViaEmail;
                promise = this.httpHelperService
                    .post<Ticket[]>(
                    url,
                    {
                        "Recipient":
                        {
                            "CustomerID": recipientInfo.CustomerID,
                            "Email": recipientInfo.Email,
                            "FirstName": recipientInfo.FirstName,
                            "LastName": recipientInfo.LastName,
                            "MobileNumber": recipientInfo.MobileNumber,
                            "Name": recipientInfo.Name,
                            "NewCustomer": false
                        },
                        "Selection":
                        {
                            "Events": null,
                            "Sponsors": null,
                            "Tickets": selectedTickets
                        },
                        "TransferType": "email"
                    },
                    {
                        //params: {
                        //    "ToEmail": recipientInfo.Email,
                        //    "CID": confirmationID,
                        //    "SendReceiptFlag": sendReceiptFlag,
                        //    "UID": 0
                        //}
                        params: {
                            "X-Token": this.tokenService.getToken(),
                            "DBName": dbName,
                            "TransferFlag": true
                        }
                    });
            } else {
                url += API_ENDPOINTS.transferViaText;
                promise = this.httpHelperService
                    .post<Ticket[]>(url,
                    {
                        "Recipient":
                        {
                            "CustomerID": recipientInfo.CustomerID,
                            "Email": recipientInfo.Email,
                            "FirstName": recipientInfo.FirstName,
                            "LastName": recipientInfo.LastName,
                            "MobileNumber": recipientInfo.MobileNumber,
                            "Name": recipientInfo.Name,
                            "NewCustomer": false
                        },
                        "Selection":
                        {
                            "Events": null,
                            "Sponsors": null,
                            "Tickets": selectedTickets
                        },
                        "TransferType": "text"
                    },
                    {
                        //params: {
                        //    "ToNumber": recipientInfo.MobileNumber,
                        //    "CID": confirmationID,
                        //    "EID": selectedTickets[0].EventID,
                        //    "SendReceiptFlag": sendReceiptFlag,
                        //    "UID": 0
                        //}
                        params: {
                            "X-Token": this.tokenService.getToken(),
                            "DBName": dbName,
                            "TransferFlag": true
                        }
                    });
            }
            return promise;   
        }   
        return Observable.empty<TransferResponse>();
    }

    getRecentRecipients(): Observable<TransferRecipient[]> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.getRecentRecipient;

        let promise = this.httpHelperService
            .get<TransferRecipient[]>(url, {
                params: {
                    "X-Token": this.tokenService.getToken(),
                    "RecentRecipientFlag": 0
                }
            });
        return promise;

    }

    printAccountTickets(dbName: string, tickets: Ticket[], validationToken: string = ""): Observable<string> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.printUrl;
        let requestParams = {
            "X-Token": this.tokenService.getToken(),
            "DBName": dbName
        }

        if (validationToken && validationToken.trim().length > 0) {
            requestParams["Token"] = validationToken;
        }

        if (dbName && tickets && tickets.length > 0) {
            let promise = this.httpHelperService
                .post<string>(url, {
                    "Tickets": tickets.map(p => {
                        return {
                            Level: p.Level,
                            Section: p.Section,
                            Row: p.Row,
                            Seat: p.Seat,
                            EventID: p.EventID,
                        }
                    })
                },
                    {
                        params: requestParams
                    });
            return promise;
        }

        return Observable.empty<string>();
    }

    displayAccountTickets(dbName: string, tickets: Ticket[], validationToken: string = ""): Observable<{ Tickets: Ticket[] }> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.displayMobileUrl;
        let requestParams = {
            "X-Token": this.tokenService.getToken(),
            "DBName": dbName
        }

        if (validationToken && validationToken.trim().length > 0) {
            requestParams["Token"] = validationToken;
        }


        if (dbName && tickets && tickets.length > 0) {
            let promise = this.httpHelperService
                .post<{ Tickets: Ticket[] }>(url, {
                    "Selection": {
                        "Tickets": tickets
                    }
                },
                    {
                        params: requestParams
                    });
            return promise;
        }

        return Observable.empty<{ Tickets: Ticket[] }>();
    }

    validateTickets(tickets: Ticket[], validationToken: string = ""): Observable<{ ResultFlag: boolean, ResultsMessage: string, Tickets: Ticket[] }> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.validateUrl;
        let requestParams = {
            "X-Token": this.tokenService.getToken()
        };

        if (validationToken && validationToken.trim().length > 0) {
            requestParams["Token"] = validationToken;
        }
        if (tickets && tickets.length > 0) {
            let promise = this.httpHelperService
                .post<{ ResultFlag: boolean, ResultsMessage: string, Tickets: Ticket[] }>(url, {
                    "Selection": {
                        "Tickets": tickets
                    }
                },
                    {
                        params: requestParams
                    });
            return promise;
        }

        return Observable.empty<{ ResultFlag: boolean, ResultsMessage: string, Tickets: Ticket[] }>();
    }

    getGoogleWalletLink(customerID: number, ticket: Ticket, validationToken: string = ""): Observable<string> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.googleWallet;
        let requestParams = {
            "X-Token": this.tokenService.getToken(),
            "GoogleFlag": 1
        }

        if (validationToken && validationToken.trim().length > 0) {
            requestParams["Token"] = validationToken;
        }
        let promise = this.httpHelperService
            .post<string>(url, {
                "CustomerID": customerID,
                "SchoolFlag": true,
                "curTicket": ticket
            },
                {
                    params: requestParams
                });
        return promise;
    }

    resetTicketTimeout(): Observable<number> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.reserveSessionSeats;
        let promise = this.httpHelperService
            .get<number>(url, {
                params: {
                    "X-Token": this.tokenService.getToken()
                }
            });

        return promise;
    }

    saveInsuranceData(data: {
        insuranceData: {
            "EventID": number,
            "Description": string,
            "Price": string
        }[],
        QuoteToken: string
        }, eventId: number, transactionTotal: number, insuranceSource: number = 2): Observable<number> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.saveInsuranceData;
        let promise = this.httpHelperService
            .post<number>(url, data, {
                params: {
                    "X-Token": this.tokenService.getToken(),
                    "EventID": eventId,
                    "TransactionTotal": transactionTotal,
                    "Source": insuranceSource
                }
            });

        return promise;
    }

    updateConfirmation(validationToken: string, transactionTotal: number, numTickets: number): Observable<string> {
        const url = UrlConstants.tokenBOUrl + API_ENDPOINTS.updateConfirmation;
        let promise = this.httpHelperService
            .get<string>(url, {
                params: {
                    "X-Token": this.tokenService.getToken(),
                    'Token': validationToken,
                    'TransactionTotal': transactionTotal,
                    'NumTickets': numTickets
                }
            });

        return promise;
    }
}



// WEBPACK FOOTER //
// ./src/services/tickets/tickets.service.ts